import {
  AUTO_THEME_DARK_END_HOUR,
  AUTO_THEME_DARK_START_HOUR,
  AUTO_THEME_MIN_BOUNDARY_DELAY_MS,
} from "../constants/theme";
import { EThemePreference } from "../types/preference";

export type TResolvedThemeScheme =
  | EThemePreference.LIGHT
  | EThemePreference.DARK;

export interface IResolveThemeSchemeProps {
  preference: EThemePreference;
  isDeviceDark: boolean;
  now?: Date;
}

export interface IThemeScheduleProps {
  now?: Date;
}

interface IBuildBoundaryProps {
  now: Date;
  hour: number;
  dayOffset: number;
}

// NOTE: NSAI-4651 — normalizes whatever a client holds for the theme into the
// tri-state. Anything unrecognized becomes `system`, which covers the two shapes
// a real client sees: an empty local cache on a fresh install, and the nullable
// `user_preferences.theme` column coming back as null on an account that never
// picked one.
//
// NOTE: NSAI-5650 — `auto` joined the set. A client still running the previous
// build parses it as `system` rather than crashing, which is the reason the
// unknown-value branch below falls back instead of throwing.
export const parseThemePreference = (
  value: string | null | undefined,
): EThemePreference => {
  if (
    value === EThemePreference.LIGHT ||
    value === EThemePreference.DARK ||
    value === EThemePreference.SYSTEM ||
    value === EThemePreference.AUTO
  ) {
    return value;
  }

  return EThemePreference.SYSTEM;
};

// NOTE: NSAI-5650 — the schedule itself: dark from 19:00 up to (not including)
// 07:00 local time. The window wraps midnight, so it is the union of two ranges
// rather than a single `start <= hour < end` comparison.
export const isScheduledDarkTime = ({
  now = new Date(),
}: IThemeScheduleProps = {}): boolean => {
  const hour = now.getHours();

  return hour >= AUTO_THEME_DARK_START_HOUR || hour < AUTO_THEME_DARK_END_HOUR;
};

const buildBoundary = ({ now, hour, dayOffset }: IBuildBoundaryProps): Date =>
  new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + dayOffset,
    hour,
    0,
    0,
    0,
  );

// NOTE: NSAI-5650 — the next local time the schedule flips, so a client can
// sleep until exactly that instant instead of polling the clock. Built from the
// calendar fields rather than by adding a fixed number of hours so it stays
// correct across month ends and DST shifts, where a day is not 24 hours long.
export const getNextThemeBoundary = ({
  now = new Date(),
}: IThemeScheduleProps = {}): Date => {
  const hour = now.getHours();

  if (hour < AUTO_THEME_DARK_END_HOUR) {
    return buildBoundary({ now, hour: AUTO_THEME_DARK_END_HOUR, dayOffset: 0 });
  }

  if (hour < AUTO_THEME_DARK_START_HOUR) {
    return buildBoundary({
      now,
      hour: AUTO_THEME_DARK_START_HOUR,
      dayOffset: 0,
    });
  }

  return buildBoundary({ now, hour: AUTO_THEME_DARK_END_HOUR, dayOffset: 1 });
};

// NOTE: NSAI-5650 — what a client hands to setTimeout. Never zero or negative:
// the caller re-arms the timer from the clock each time it fires, so a delay
// that has already elapsed must still push the next check into the future
// rather than schedule a same-tick callback that re-arms with zero again.
//
// NOTE: NSAI-5650 — the finite check is what makes that guarantee hold for an
// invalid `now`, where the subtraction is NaN. `Math.max(NaN, floor)` is NaN,
// and `setTimeout` coerces NaN to 0 — so without this the one input the floor
// exists to defend against would produce exactly the tight loop it promises to
// prevent. Unreachable from the clients today (they all let `now` default to
// `new Date()`), which is precisely why it needed pinning rather than trusting.
export const getMsUntilNextThemeBoundary = ({
  now = new Date(),
}: IThemeScheduleProps = {}): number => {
  const delay = getNextThemeBoundary({ now }).getTime() - now.getTime();

  if (!Number.isFinite(delay)) {
    return AUTO_THEME_MIN_BOUNDARY_DELAY_MS;
  }

  return Math.max(delay, AUTO_THEME_MIN_BOUNDARY_DELAY_MS);
};

// NOTE: NSAI-4651 — the single rule for turning the tri-state preference into
// the scheme actually rendered. Only `system` defers to the device, so both the
// signed-out and signed-in halves of a client must run the preference through
// here rather than one of them reading the device directly: that mismatch is
// what repainted the mobile app the instant a user authenticated.
//
// NOTE: NSAI-5650 — `auto` defers to the clock instead of the device. It reads
// the time on every call, so a caller that wants the UI to actually follow the
// schedule has to re-run this at the boundary (see getMsUntilNextThemeBoundary)
// — resolving once at mount answers only for the moment the app started.
export const resolveThemeScheme = ({
  preference,
  isDeviceDark,
  now,
}: IResolveThemeSchemeProps): TResolvedThemeScheme => {
  if (preference === EThemePreference.LIGHT) {
    return EThemePreference.LIGHT;
  }

  if (preference === EThemePreference.DARK) {
    return EThemePreference.DARK;
  }

  if (preference === EThemePreference.AUTO) {
    return isScheduledDarkTime({ now })
      ? EThemePreference.DARK
      : EThemePreference.LIGHT;
  }

  return isDeviceDark ? EThemePreference.DARK : EThemePreference.LIGHT;
};
